import { Block } from "../engine/blocks";
import { Item } from "./items";
import type { SlotCell } from "./inventory";
import { makeSlot } from "./inventory";

export type Pattern = (number | null)[][];

export interface Recipe {
  id: string;
  shapeless?: number[];
  pattern?: Pattern;
  result: { id: number; count: number };
}

function row(...cells: (number | null)[]): (number | null)[] {
  return cells;
}

export const RECIPES: Recipe[] = [
  {
    id: "oak-planks",
    shapeless: [Block.OAK_LOG],
    result: { id: Block.OAK_PLANKS, count: 4 },
  },
  {
    id: "birch-planks",
    shapeless: [Block.BIRCH_LOG],
    result: { id: Block.BIRCH_PLANKS, count: 4 },
  },
  {
    id: "pine-planks",
    shapeless: [Block.PINE_LOG],
    result: { id: Block.OAK_PLANKS, count: 4 },
  },
  {
    id: "cherry-planks",
    shapeless: [Block.CHERRY_LOG],
    result: { id: Block.OAK_PLANKS, count: 4 },
  },
  {
    id: "jungle-planks",
    shapeless: [Block.JUNGLE_LOG],
    result: { id: Block.OAK_PLANKS, count: 4 },
  },
  {
    id: "sticks",
    pattern: [row(Block.OAK_PLANKS), row(Block.OAK_PLANKS)],
    result: { id: Item.STICK, count: 4 },
  },
  {
    id: "sticks-birch",
    pattern: [row(Block.BIRCH_PLANKS), row(Block.BIRCH_PLANKS)],
    result: { id: Item.STICK, count: 4 },
  },
  {
    id: "crafting-table",
    pattern: [
      row(Block.OAK_PLANKS, Block.OAK_PLANKS),
      row(Block.OAK_PLANKS, Block.OAK_PLANKS),
    ],
    result: { id: Block.CRAFTING_TABLE, count: 1 },
  },
  {
    id: "chest",
    pattern: [
      row(Block.OAK_PLANKS, Block.OAK_PLANKS, Block.OAK_PLANKS),
      row(Block.OAK_PLANKS, null, Block.OAK_PLANKS),
      row(Block.OAK_PLANKS, Block.OAK_PLANKS, Block.OAK_PLANKS),
    ],
    result: { id: Block.CHEST, count: 1 },
  },
  {
    id: "furnace",
    pattern: [
      row(Block.COBBLE, Block.COBBLE, Block.COBBLE),
      row(Block.COBBLE, null, Block.COBBLE),
      row(Block.COBBLE, Block.COBBLE, Block.COBBLE),
    ],
    result: { id: Block.FURNACE, count: 1 },
  },
  {
    id: "bread",
    pattern: [row(Item.WHEAT, Item.WHEAT, Item.WHEAT)],
    result: { id: Item.BREAD, count: 1 },
  },
  {
    id: "sandstone",
    pattern: [row(Block.SAND, Block.SAND), row(Block.SAND, Block.SAND)],
    result: { id: Block.SANDSTONE, count: 1 },
  },
  {
    id: "iron-block",
    pattern: [
      row(Item.IRON_INGOT, Item.IRON_INGOT, Item.IRON_INGOT),
      row(Item.IRON_INGOT, Item.IRON_INGOT, Item.IRON_INGOT),
      row(Item.IRON_INGOT, Item.IRON_INGOT, Item.IRON_INGOT),
    ],
    result: { id: Block.IRON_BLOCK, count: 1 },
  },
  {
    id: "gold-block",
    pattern: [
      row(Item.GOLD_INGOT, Item.GOLD_INGOT, Item.GOLD_INGOT),
      row(Item.GOLD_INGOT, Item.GOLD_INGOT, Item.GOLD_INGOT),
      row(Item.GOLD_INGOT, Item.GOLD_INGOT, Item.GOLD_INGOT),
    ],
    result: { id: Block.GOLD_BLOCK, count: 1 },
  },
  {
    id: "diamond-block",
    pattern: [
      row(Item.DIAMOND, Item.DIAMOND, Item.DIAMOND),
      row(Item.DIAMOND, Item.DIAMOND, Item.DIAMOND),
      row(Item.DIAMOND, Item.DIAMOND, Item.DIAMOND),
    ],
    result: { id: Block.DIAMOND_BLOCK, count: 1 },
  },
  ...toolset("wood", Block.OAK_PLANKS, Item.WOOD_SWORD, Item.WOOD_PICK, Item.WOOD_AXE, Item.WOOD_SHOVEL),
  ...toolset("stone", Block.COBBLE, Item.STONE_SWORD, Item.STONE_PICK, Item.STONE_AXE, Item.STONE_SHOVEL),
  ...toolset("iron", Item.IRON_INGOT, Item.IRON_SWORD, Item.IRON_PICK, Item.IRON_AXE, Item.IRON_SHOVEL),
  ...toolset("gold", Item.GOLD_INGOT, Item.GOLD_SWORD, Item.GOLD_PICK, Item.GOLD_AXE, Item.GOLD_SHOVEL),
  ...toolset("dia", Item.DIAMOND, Item.DIAMOND_SWORD, Item.DIAMOND_PICK, Item.DIAMOND_AXE, Item.DIAMOND_SHOVEL),
  ...armorSet("leather", Item.LEATHER, Item.LEATHER_HELM, Item.LEATHER_CHEST, Item.LEATHER_LEGS, Item.LEATHER_BOOTS),
  ...armorSet("iron", Item.IRON_INGOT, Item.IRON_HELM, Item.IRON_CHEST, Item.IRON_LEGS, Item.IRON_BOOTS),
  ...armorSet("gold", Item.GOLD_INGOT, Item.GOLD_HELM, Item.GOLD_CHEST, Item.GOLD_LEGS, Item.GOLD_BOOTS),
  ...armorSet("dia", Item.DIAMOND, Item.DIAMOND_HELM, Item.DIAMOND_CHEST, Item.DIAMOND_LEGS, Item.DIAMOND_BOOTS),
];

function toolset(
  name: string,
  mat: number,
  sword: number,
  pick: number,
  axe: number,
  shovel: number,
): Recipe[] {
  const s = Item.STICK;
  return [
    {
      id: `${name}-sword`,
      pattern: [row(mat), row(mat), row(s)],
      result: { id: sword, count: 1 },
    },
    {
      id: `${name}-pick`,
      pattern: [row(mat, mat, mat), row(null, s, null), row(null, s, null)],
      result: { id: pick, count: 1 },
    },
    {
      id: `${name}-axe`,
      pattern: [row(mat, mat), row(mat, s), row(null, s)],
      result: { id: axe, count: 1 },
    },
    {
      id: `${name}-shovel`,
      pattern: [row(mat), row(s), row(s)],
      result: { id: shovel, count: 1 },
    },
  ];
}

function armorSet(
  name: string,
  mat: number,
  helm: number,
  chest: number,
  legs: number,
  boots: number,
): Recipe[] {
  return [
    {
      id: `${name}-helm`,
      pattern: [row(mat, mat, mat), row(mat, null, mat)],
      result: { id: helm, count: 1 },
    },
    {
      id: `${name}-chest`,
      pattern: [row(mat, null, mat), row(mat, mat, mat), row(mat, mat, mat)],
      result: { id: chest, count: 1 },
    },
    {
      id: `${name}-legs`,
      pattern: [row(mat, mat, mat), row(mat, null, mat), row(mat, null, mat)],
      result: { id: legs, count: 1 },
    },
    {
      id: `${name}-boots`,
      pattern: [row(mat, null, mat), row(mat, null, mat)],
      result: { id: boots, count: 1 },
    },
  ];
}

export const SMELT: { input: number; output: number; time: number }[] = [
  { input: Block.IRON_ORE, output: Item.IRON_INGOT, time: 8 },
  { input: Block.GOLD_ORE, output: Item.GOLD_INGOT, time: 8 },
  { input: Block.COBBLE, output: Block.STONE, time: 8 },
  { input: Block.SAND, output: Block.GLASS, time: 8 },
  { input: Block.OAK_LOG, output: Item.COAL, time: 8 },
  { input: Block.BIRCH_LOG, output: Item.COAL, time: 8 },
  { input: Block.PINE_LOG, output: Item.COAL, time: 8 },
  { input: Block.CHERRY_LOG, output: Item.COAL, time: 8 },
  { input: Block.JUNGLE_LOG, output: Item.COAL, time: 8 },
  { input: Item.RAW_PORK, output: Item.COOKED_PORK, time: 8 },
  { input: Block.CLAY, output: Block.BRICKS, time: 8 },
];

export function fuelValue(id: number): number {
  if (id === Item.COAL) return 8;
  if (id === Item.STICK) return 1;
  if (id === Block.OAK_LOG || id === Block.BIRCH_LOG || id === Block.PINE_LOG) return 1.5;
  if (id === Block.OAK_PLANKS || id === Block.BIRCH_PLANKS || id === Block.CRAFTING_TABLE) return 1.5;
  if (id === Block.CHEST) return 1.5;
  return 0;
}

export function smeltOf(id: number): (typeof SMELT)[number] | undefined {
  return SMELT.find((s) => s.input === id);
}

function gridIds(slots: SlotCell[], width: number): (number | null)[][] {
  const height = Math.ceil(slots.length / width);
  const g: (number | null)[][] = [];
  for (let y = 0; y < height; y++) {
    const r: (number | null)[] = [];
    for (let x = 0; x < width; x++) {
      const s = slots[y * width + x];
      r.push(s ? s.id : null);
    }
    g.push(r);
  }
  return g;
}

function trim(grid: (number | null)[][]): (number | null)[][] {
  if (grid.length === 0) return grid;
  let minX = 99;
  let minY = 99;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[y]!.length; x++) {
      if (grid[y]![x] !== null) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < 0) return [];
  const out: (number | null)[][] = [];
  for (let y = minY; y <= maxY; y++) {
    out.push(grid[y]!.slice(minX, maxX + 1));
  }
  return out;
}

function matchPattern(grid: (number | null)[][], pattern: Pattern): boolean {
  const g = trim(grid);
  const p = pattern;
  if (g.length !== p.length) return false;
  if ((g[0]?.length ?? 0) !== (p[0]?.length ?? 0)) return false;
  for (let y = 0; y < p.length; y++) {
    for (let x = 0; x < p[y]!.length; x++) {
      if ((g[y]![x] ?? null) !== (p[y]![x] ?? null)) return false;
    }
  }
  return true;
}

function matchShapeless(slots: SlotCell[], need: number[]): boolean {
  const have = new Map<number, number>();
  let total = 0;
  for (const s of slots) {
    if (!s) continue;
    have.set(s.id, (have.get(s.id) ?? 0) + 1);
    total += 1;
  }
  if (total !== need.length) return false;
  const want = new Map<number, number>();
  for (const id of need) want.set(id, (want.get(id) ?? 0) + 1);
  if (want.size !== have.size) return false;
  for (const [id, n] of want) {
    if (have.get(id) !== n) return false;
  }
  return true;
}

export function matchRecipe(slots: SlotCell[], width: number): Recipe | null {
  const grid = gridIds(slots, width);
  const any = slots.some((s) => s !== null);
  if (!any) return null;
  for (const r of RECIPES) {
    if (r.shapeless && matchShapeless(slots, r.shapeless)) return r;
    if (r.pattern && matchPattern(grid, r.pattern)) return r;
  }
  return null;
}

export function resultSlot(slots: SlotCell[], width: number): SlotCell {
  const r = matchRecipe(slots, width);
  if (!r) return null;
  return makeSlot(r.result.id, r.result.count);
}
